import React, { useCallback, useEffect, useState } from 'react'
import { InputWrapper } from '@/components/Input.styles'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import TextField from '@/components/TextField'
import InputError from '@/components/InputError'
import { Spacing } from '@big-red-group/storefront-common-ui'
import Select from '@/components/Select'
import postCodes from '@/json/postCodes.json'
import { PostCodeResponse } from '@/types'
import debounce from 'lodash.debounce'
import { VoucherPostageOptions } from '@big-red-group/storefront-common-checkout'

const VoucherPostageForm: React.FC<{
  setPostageOptions: (options: VoucherPostageOptions) => void
  setHasCompletedPostForm: (k: boolean) => void
}> = ({ setPostageOptions, setHasCompletedPostForm }) => {
  const [selectedSuburb, setSelectedSuburb] = useState<{ label: string; value: string }[]>([])
  const [postState, setPostState] = useState('')

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required('First name is required.'),
    lastName: Yup.string().required('Last name is required'),
    address: Yup.string().required('Address is required'),
    postcode: Yup.string().required('Postcode is required'),
    suburb: Yup.string().required('Suburb is required'),
  })

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      address: '',
      suburb: '',
      postcode: '',
    },
    validationSchema,
    onSubmit: async () => {},
  })

  const handlePostCodeChange = async () => {
    const postCodeValue = formik.values.postcode
    const postCodeData =
      postCodeValue.length === 4 && postCodes.find((postcode: PostCodeResponse) => postcode.code == postCodeValue)

    if (postCodeData) {
      const suburbDetails = postCodeData.details?.map(({ suburb }) => ({ label: suburb, value: suburb }))
      setSelectedSuburb(suburbDetails ? suburbDetails : [])
      formik.setFieldValue('suburb', suburbDetails?.[0]?.value)
      setPostState(postCodeData?.details[0]?.state)
    } else {
      setSelectedSuburb([])
      if (postCodeValue.length === 4) {
        formik.setFieldValue('postcode', '')
      }
    }
  }

  const handleSuburbChange = async (event: string) => {
    await formik.setFieldValue('suburb', event)
  }

  const handleFormChange = useCallback(
    debounce(async (options: VoucherPostageOptions) => {
      setPostageOptions(options)
      await formik.validateForm()
      const hasErrors = Object.keys(formik.errors).length > 0
      setHasCompletedPostForm(!hasErrors)
    }, 100) as Function,
    [formik.errors, setPostageOptions, setHasCompletedPostForm]
  )

  useEffect(() => {
    handlePostCodeChange()
  }, [formik.values.postcode])

  useEffect(() => {
    handleFormChange(formik.values)
  }, [formik.values, formik.errors])

  return (
    <>
      <InputWrapper>
        <label className="required">Recipient first name</label>
        <TextField
          name="firstName"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="First name"
          error={!!(formik.touched.firstName && formik.errors.firstName)}
        />
        {formik.touched.firstName && formik.errors.firstName && (
          <InputError dataTestId="firstName-error-message">{formik.errors.firstName}</InputError>
        )}
      </InputWrapper>
      <Spacing size="S" />
      <InputWrapper>
        <label className="required">Recipient last name</label>
        <TextField
          name="lastName"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Last name"
          error={!!(formik.touched.lastName && formik.errors.lastName)}
        />
        {formik.touched.lastName && formik.errors.lastName && (
          <InputError dataTestId="email-error-message">{formik.errors.lastName}</InputError>
        )}
      </InputWrapper>
      <Spacing size="S" />
      <InputWrapper>
        <label className="required">Recipient address</label>
        <TextField
          name="address"
          value={formik.values.address}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Address"
          error={!!(formik.touched.address && formik.errors.address)}
        />
      </InputWrapper>
      <Spacing size="S" />
      <InputWrapper>
        <label className="required">Recipient postcode</label>
        <TextField
          name="postcode"
          value={formik.values.postcode}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Postcode"
          error={!!(formik.touched.postcode && formik.errors.postcode)}
        />
      </InputWrapper>
      <Spacing size="S" />
      <InputWrapper>
        <label>Suburb</label>
        <Select
          name="suburb"
          options={selectedSuburb}
          value={formik.values.suburb}
          onChange={(event) => handleSuburbChange(event)}
        />
      </InputWrapper>
    </>
  )
}

export default VoucherPostageForm
